import { defineStore } from 'pinia';
import { computed, ref } from '#imports';
import {
  AccountVariablesDto,
  AccountWithOrganizationDto,
  apiCloseWelcomeScreen,
  apiGetCurrentAccount,
  apiPatchAccountVariables,
} from '~/api/accounts';
import { FetchError } from 'ofetch';
import { useOrganizationStore } from './organization';
import { Nullable } from '~/types/common';

export const ACCOUNT_FRESH_MS = 60_000;
export const ACCOUNT_MAX_AGE_MS = 600_000;
export const WELCOME_SCREEN_ALREADY_CLOSED =
  'ACCOUNT_WELCOME_SCREEN_ALREADY_CLOSED';

const isWelcomeScreenAlreadyClosed = (err: unknown): boolean =>
  err instanceof FetchError &&
  err.data?.error === WELCOME_SCREEN_ALREADY_CLOSED;

type PendingAccountLoad = {
  organizationId: Nullable<number>;
  promise: Promise<void>;
};

export type EnsureCurrentAccountOptions = {
  organizationId: number;
  onBackgroundFailure: (reason: unknown) => void | Promise<void>;
};

export const useAccountStore = defineStore('account', () => {
  const organizationStore = useOrganizationStore();

  const currentAccount = ref<Nullable<AccountWithOrganizationDto>>(null);
  const currentAccountOrganizationId = ref<Nullable<number>>(null);
  const accountVariables = computed(() => ({
    phoneNumber: currentAccount.value?.phoneNumberVariable,
    website: currentAccount.value?.websiteVariable,
    company: currentAccount.value?.companyVariable,
  }));

  let loadedAt: Nullable<number> = null;
  let pendingLoad: Nullable<PendingAccountLoad> = null;
  let startedLoads = 0;
  let newestStoredLoad = 0;

  const loadCurrentAccount = (): Promise<void> => {
    startedLoads += 1;
    const loadNumber = startedLoads;
    const organizationId = organizationStore.organization?.id ?? null;

    const promise = apiGetCurrentAccount()
      .then((account) => {
        if (loadNumber <= newestStoredLoad) {
          return;
        }
        newestStoredLoad = loadNumber;
        currentAccount.value = account;
        currentAccountOrganizationId.value = organizationId;
        loadedAt = Date.now();
      })
      .finally(() => {
        if (pendingLoad?.promise === promise) {
          pendingLoad = null;
        }
      });

    pendingLoad = { organizationId, promise };
    return promise;
  };

  const setCurrentAccount = (): Promise<void> => loadCurrentAccount();

  const refreshCurrentAccount = (): Promise<void> => loadCurrentAccount();

  const ensureCurrentAccount = ({
    organizationId,
    onBackgroundFailure,
  }: EnsureCurrentAccountOptions): Promise<void> => {
    const pendingForOrganization =
      pendingLoad?.organizationId === organizationId
        ? pendingLoad.promise
        : null;

    if (
      currentAccount.value === null ||
      currentAccountOrganizationId.value !== organizationId
    ) {
      return pendingForOrganization ?? loadCurrentAccount();
    }

    loadedAt ??= Date.now();
    const age = Date.now() - loadedAt;

    if (age > ACCOUNT_MAX_AGE_MS) {
      return pendingForOrganization ?? loadCurrentAccount();
    }

    if (age > ACCOUNT_FRESH_MS && pendingForOrganization === null) {
      loadCurrentAccount().catch(onBackgroundFailure);
    }

    return Promise.resolve();
  };

  const clearCurrentAccount = (): void => {
    newestStoredLoad = startedLoads;
    pendingLoad = null;
    loadedAt = null;
    currentAccount.value = null;
    currentAccountOrganizationId.value = null;
  };

  const storeAccountChanges = (
    changes: Partial<AccountWithOrganizationDto>,
  ): void => {
    if (currentAccount.value === null) {
      return;
    }
    newestStoredLoad = startedLoads;
    pendingLoad = null;
    currentAccount.value = { ...currentAccount.value, ...changes };
  };

  const updateAccountVariables = async (
    variables: AccountVariablesDto,
  ): Promise<void> => {
    const { websiteVariable, phoneNumberVariable, companyVariable } =
      await apiPatchAccountVariables(variables);
    storeAccountChanges({
      websiteVariable,
      phoneNumberVariable,
      companyVariable,
    });
  };

  const closeWelcomeScreen = async (): Promise<void> => {
    try {
      const { welcomeScreenClosedAt } = await apiCloseWelcomeScreen();
      storeAccountChanges({ welcomeScreenClosedAt });
    } catch (err) {
      if (!isWelcomeScreenAlreadyClosed(err)) {
        throw err;
      }
      await loadCurrentAccount();
    }
  };

  return {
    setCurrentAccount,
    refreshCurrentAccount,
    ensureCurrentAccount,
    clearCurrentAccount,
    updateAccountVariables,
    closeWelcomeScreen,
    accountVariables,
    currentAccount,
    currentAccountOrganizationId,
  };
});
